import React, { useState, useEffect } from 'react'
import { Card, FormGroup, Label, Input, Button, Table, Row, Col, FormFeedback, Badge, Progress } from 'reactstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'

const RISK_CATEGORIES = [
  { value: 'medical', label: 'Medical History' },
  { value: 'family', label: 'Family History' },
  { value: 'lifestyle', label: 'Lifestyle Factors' },
  { value: 'environmental', label: 'Environmental Factors' },
  { value: 'biometric', label: 'Biometric Measurements' }
]

const RISK_LEVELS = [
  { value: 'high', label: 'High', color: 'danger', weight: 3 },
  { value: 'medium', label: 'Medium', color: 'warning', weight: 2 },
  { value: 'low', label: 'Low', color: 'info', weight: 1 },
  { value: 'none', label: 'None', color: 'success', weight: 0 }
]

const RiskFactorAssessment = ({ onChange, value = [] }) => {
  const [riskFactors, setRiskFactors] = useState(value || [])
  const [editing, setEditing] = useState(false)
  const [editIndex, setEditIndex] = useState(null)
  const [overallRisk, setOverallRisk] = useState({
    score: 0,
    level: 'none',
    percentage: 0
  })

  useEffect(() => {
    if (onChange && riskFactors) {
      onChange(riskFactors)
    }

    // Calculate overall risk
    calculateOverallRisk()
    // eslint-disable-next-line
  }, [riskFactors, onChange])

  const calculateOverallRisk = () => {
    if (riskFactors.length === 0) {
      setOverallRisk({ score: 0, level: 'none', percentage: 0 })
      return
    }

    const totalWeight = riskFactors.reduce((sum, factor) => {
      const level = RISK_LEVELS.find((l) => l.value === factor.riskLevel)
      return sum + (level ? level.weight : 0)
    }, 0)

    const maxPossibleWeight = riskFactors.length * 3 // 3 is the weight of high risk
    const percentage = Math.round((totalWeight / maxPossibleWeight) * 100)

    let level = 'none'
    if (percentage >= 70) level = 'high'
    else if (percentage >= 40) level = 'medium'
    else if (percentage > 0) level = 'low'

    setOverallRisk({
      score: totalWeight,
      level,
      percentage
    })
  }

  const validationSchema = Yup.object({
    factorName: Yup.string().required('Risk factor name is required'),
    category: Yup.string().required('Category is required'),
    riskLevel: Yup.string().required('Risk level is required'),
    description: Yup.string().nullable()
  })

  const formik = useFormik({
    initialValues: {
      factorName: '',
      category: '',
      riskLevel: 'medium',
      description: '',
      mitigationStrategy: ''
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      if (editing && editIndex !== null) {
        const updatedRiskFactors = [...riskFactors]
        updatedRiskFactors[editIndex] = values
        setRiskFactors(updatedRiskFactors)
        setEditing(false)
        setEditIndex(null)
      } else {
        setRiskFactors([...riskFactors, values])
      }
      resetForm()
    }
  })

  const handleEdit = (index) => {
    setEditing(true)
    setEditIndex(index)
    formik.setValues(riskFactors[index])
  }

  const handleDelete = (index) => {
    const filteredRiskFactors = riskFactors.filter((_, i) => i !== index)
    setRiskFactors(filteredRiskFactors)
  }

  const handleCancel = () => {
    setEditing(false)
    setEditIndex(null)
    formik.resetForm()
  }

  const getCategoryLabel = (categoryValue) => {
    const category = RISK_CATEGORIES.find((c) => c.value === categoryValue)
    return category ? category.label : categoryValue
  }

  const getRiskLevelBadge = (riskLevel) => {
    const level = RISK_LEVELS.find((l) => l.value === riskLevel)
    return level ? <Badge color={level.color}>{level.label}</Badge> : riskLevel
  }

  const getProgressColor = (level) => {
    switch (level) {
      case 'high':
        return 'danger'
      case 'medium':
        return 'warning'
      case 'low':
        return 'info'
      default:
        return 'success'
    }
  }

  return (
    <Card className='p-4'>
      <h4 className='mb-4'>Risk Factor Assessment</h4>

      {riskFactors.length > 0 && (
        <div className='mb-4'>
          <h5>Overall Risk Profile</h5>
          <Row>
            <Col md={8}>
              <Progress
                value={overallRisk.percentage}
                color={getProgressColor(overallRisk.level)}
                className='mb-2'
                style={{ height: '25px' }}
              >
                {overallRisk.percentage}%
              </Progress>
            </Col>
            <Col md={4}>
              <h6>Risk Level: {getRiskLevelBadge(overallRisk.level)}</h6>
            </Col>
          </Row>
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for='factorName'>Risk Factor Name</Label>
              <Input
                type='text'
                id='factorName'
                name='factorName'
                placeholder='Enter risk factor name'
                value={formik.values.factorName}
                onChange={formik.handleChange}
                invalid={formik.touched.factorName && formik.errors.factorName}
              />
              {formik.touched.factorName && formik.errors.factorName && (
                <FormFeedback>{formik.errors.factorName}</FormFeedback>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for='category'>Category</Label>
              <Input
                type='select'
                id='category'
                name='category'
                value={formik.values.category}
                onChange={formik.handleChange}
                invalid={formik.touched.category && formik.errors.category}
              >
                <option value=''>Select category</option>
                {RISK_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </Input>
              {formik.touched.category && formik.errors.category && (
                <FormFeedback>{formik.errors.category}</FormFeedback>
              )}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for='riskLevel'>Risk Level</Label>
              <Input
                type='select'
                id='riskLevel'
                name='riskLevel'
                value={formik.values.riskLevel}
                onChange={formik.handleChange}
                invalid={formik.touched.riskLevel && formik.errors.riskLevel}
              >
                {RISK_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </Input>
              {formik.touched.riskLevel && formik.errors.riskLevel && (
                <FormFeedback>{formik.errors.riskLevel}</FormFeedback>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for='description'>Description</Label>
              <Input
                type='textarea'
                id='description'
                name='description'
                placeholder='Enter risk factor description'
                value={formik.values.description}
                onChange={formik.handleChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label for='mitigationStrategy'>Mitigation Strategy</Label>
              <Input
                type='textarea'
                id='mitigationStrategy'
                name='mitigationStrategy'
                placeholder='Enter strategies to mitigate this risk factor'
                rows='3'
                value={formik.values.mitigationStrategy}
                onChange={formik.handleChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <div className='d-flex'>
          <Button type='submit' color='primary' className='mr-2'>
            {editing ? 'Update Risk Factor' : 'Add Risk Factor'}
          </Button>
          {editing && (
            <Button type='button' color='secondary' onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </div>
      </form>

      {riskFactors.length > 0 && (
        <div className='mt-4'>
          <h5>Identified Risk Factors</h5>
          <Table striped responsive>
            <thead>
              <tr>
                <th>Risk Factor</th>
                <th>Category</th>
                <th>Risk Level</th>
                <th>Mitigation Strategy</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {riskFactors.map((factor, index) => (
                <tr key={index}>
                  <td>{factor.factorName}</td>
                  <td>{getCategoryLabel(factor.category)}</td>
                  <td>{getRiskLevelBadge(factor.riskLevel)}</td>
                  <td>{factor.mitigationStrategy}</td>
                  <td>
                    <Button color='info' size='sm' className='mr-1' onClick={() => handleEdit(index)}>
                      Edit
                    </Button>
                    <Button color='danger' size='sm' onClick={() => handleDelete(index)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  )
}

export default RiskFactorAssessment
